/**
 * What to call somebody.
 *
 * A new account is minted with the display name `guest` — that is the server's default
 * in `internal/social/store.ts`, and it means "has never said". Rendering it produces
 * sentences like "You and guest are friends" and "Removed guest", which are worse than
 * useless in a household where three people have not opened the Profile screen yet.
 *
 * So an unset name falls back to the handle, which is always set, always unique and
 * always sayable out loud. `@amber-otter` beats `guest` every time.
 *
 * ONE FUNCTION, USED EVERYWHERE A PERSON IS NAMED, including inside the store where the
 * confirmation sentences are built — otherwise a row would say `@amber-otter` and the
 * notice under it would say `guest` about the same press.
 */

/** The server's minted default. Not a name anybody chose. */
export const UNSET_NAME = 'guest';

export interface Named {
  displayName?: string;
  handle?: string;
}

/** The name to show for a person: what they chose, or their handle. */
export function personName(who: Named | null | undefined): string {
  const chosen = who?.displayName?.trim() ?? '';
  if (chosen !== '' && chosen !== UNSET_NAME) return chosen;
  const handle = who?.handle?.trim() ?? '';
  return handle === '' ? 'Someone' : `@${handle}`;
}

/** True when this person has never chosen a name. The Profile screen asks for one. */
export function unnamed(who: Named | null | undefined): boolean {
  const chosen = who?.displayName?.trim() ?? '';
  return chosen === '' || chosen === UNSET_NAME;
}
